import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";

const MONTHS = ["January","February","March","April","May","June",
  "July","August","September","October","November","December"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"];

const WEEKDAY_NUMS = {
  sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6,
};
const OCCURRENCE_LABELS = { first:"1st", second:"2nd", third:"3rd", fourth:"4th", last:"Last" };
const WEEKDAY_LABELS = {
  monday:"Mon", tuesday:"Tue", wednesday:"Wed", thursday:"Thu",
  friday:"Fri", saturday:"Sat", sunday:"Sun",
};

function computeScheduleDate(year, month, weekday, occurrence) {
  const wdNum = WEEKDAY_NUMS[weekday];
  if (occurrence === "last") {
    const lastDay = new Date(year, month + 1, 0);
    const diff = (lastDay.getDay() - wdNum + 7) % 7;
    return new Date(year, month, lastDay.getDate() - diff);
  }
  const firstDay = new Date(year, month, 1);
  const diff = (wdNum - firstDay.getDay() + 7) % 7;
  const nthMap = { first:0, second:1, third:2, fourth:3 };
  return new Date(year, month, 1 + diff + (nthMap[occurrence] || 0) * 7);
}

function toDatetimeLocal(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T09:00`;
}

const STATUS_CYCLE = { due: "overdue", overdue: "scheduled", scheduled: "done", done: "due" };
const STATUS_CFG = {
  due:       { label: "Due",       bg: "#eff6ff", color: "#1e40af", accent: "#93c5fd" },
  overdue:   { label: "Overdue",   bg: "#fee2e2", color: "#dc2626", accent: "#dc2626" },
  scheduled: { label: "Scheduled", bg: "#fef3c7", color: "#d97706", accent: "#d97706" },
  done:      { label: "Done",      bg: "#dcfce7", color: "#16a34a", accent: "#16a34a" },
};

const ROW_GRID = "2fr 1.5fr 90px 90px 160px 190px";

const iconBtnStyle = {
  background: "none", border: `1px solid ${colors.border}`,
  borderRadius: "6px", cursor: "pointer", color: colors.text,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: "0", transition: "all 0.15s",
};

function KpiChip({ label, value, color, bg, textColor }) {
  return (
    <div style={{
      background: bg || colors.white,
      border: `1px solid ${colors.border}`,
      borderTop: `3px solid ${color}`,
      borderRadius: "10px",
      padding: "12px 16px",
      minWidth: "100px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      flex: 1,
    }}>
      <div style={{ fontSize: "20px", fontWeight: 800, color: textColor || color, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: "11px", color: textColor ? "rgba(255,255,255,0.7)" : colors.slate, marginTop: "4px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
    </div>
  );
}

function StatusPill({ status, onClick, loading }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.due;
  return (
    <span
      onClick={!loading ? onClick : undefined}
      title="Click to cycle status"
      style={{
        fontSize: "11px", fontWeight: 700, padding: "4px 10px",
        borderRadius: "20px", backgroundColor: cfg.bg, color: cfg.color,
        cursor: loading ? "default" : "pointer",
        opacity: loading ? 0.5 : 1,
        userSelect: "none", whiteSpace: "nowrap",
        transition: "opacity 0.15s",
        display: "inline-block",
      }}
    >{cfg.label}</span>
  );
}

function SiteRow({ site, record, monthNum, year, onUpdated, onSiteUpdated }) {
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dateVal, setDateVal] = useState(
    record?.scheduled_at ? record.scheduled_at.slice(0, 16) : ""
  );
  const [editRule, setEditRule] = useState(false);
  const [ruleOcc, setRuleOcc] = useState(site.monthly_schedule_occurrence || "");
  const [ruleWd,  setRuleWd]  = useState(site.monthly_schedule_weekday    || "");
  const [savingRule, setSavingRule] = useState(false);

  useEffect(() => {
    setDateVal(record?.scheduled_at ? record.scheduled_at.slice(0, 16) : "");
  }, [record?.scheduled_at]);

  useEffect(() => {
    setRuleOcc(site.monthly_schedule_occurrence || "");
    setRuleWd(site.monthly_schedule_weekday    || "");
  }, [site.monthly_schedule_occurrence, site.monthly_schedule_weekday]);

  const currentStatus = record?.status || "due";
  const statusCfg = STATUS_CFG[currentStatus] || STATUS_CFG.due;

  const upsert = async (patch) => {
    setSaving(true);
    try {
      if (record) {
        const res = await axios.patch(`${API}/monthly-services/${record.id}/`, patch);
        onUpdated(res.data);
      } else {
        const res = await axios.post(`${API}/monthly-services/`, {
          site: site.id, year, month: monthNum, ...patch,
        });
        onUpdated(res.data);
      }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleToggleStatus = () => upsert({ status: STATUS_CYCLE[currentStatus] || "due" });

  const handleDateChange = async (e) => {
    const val = e.target.value;
    setDateVal(val);
    if (!val) { await upsert({ scheduled_at: null }); return; }
    const patch = { scheduled_at: val };
    if (currentStatus === "due" || currentStatus === "overdue") patch.status = "scheduled";
    await upsert(patch);
  };

  const handleSaveRule = async () => {
    setSavingRule(true);
    try {
      await axios.patch(`${API}/sites/${site.id}/`, {
        monthly_schedule_occurrence: ruleOcc,
        monthly_schedule_weekday:    ruleWd,
      });
      onSiteUpdated({ ...site, monthly_schedule_occurrence: ruleOcc, monthly_schedule_weekday: ruleWd });
      setEditRule(false);
    } catch { /* ignore */ }
    finally { setSavingRule(false); }
  };

  const handleCancelRule = () => {
    setEditRule(false);
    setRuleOcc(site.monthly_schedule_occurrence || "");
    setRuleWd(site.monthly_schedule_weekday    || "");
  };

  const hasRule = site.monthly_schedule_weekday && site.monthly_schedule_occurrence;
  const suggestedDate = hasRule
    ? computeScheduleDate(year, monthNum - 1, site.monthly_schedule_weekday, site.monthly_schedule_occurrence)
    : null;
  const suggestedStr = suggestedDate ? toDatetimeLocal(suggestedDate) : null;

  const handleUseSchedule = () => {
    const patch = { scheduled_at: suggestedStr };
    if (currentStatus === "due" || currentStatus === "overdue") patch.status = "scheduled";
    setDateVal(suggestedStr);
    upsert(patch);
  };

  const feeValue = site.monthly_fee && parseFloat(site.monthly_fee) > 0
    ? parseFloat(site.monthly_fee) : null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: ROW_GRID,
        alignItems: "start",
        gap: "12px",
        padding: "12px 20px",
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: hovered ? "#f8fafc" : colors.white,
        borderLeft: `3px solid ${statusCfg.accent}`,
        transition: "background-color 0.12s",
      }}
    >
      {/* Site */}
      <div style={{ paddingTop: "2px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, color: colors.text }}>
          {site.site_name}
        </div>
        <div style={{ fontSize: "12px", color: colors.slate, marginTop: "2px", lineHeight: 1.3 }}>
          {site.site_address}
        </div>
      </div>

      {/* Account */}
      <div style={{ fontSize: "13px", color: colors.slate, paddingTop: "4px" }}>
        {site.account_name}
      </div>

      {/* Value */}
      <div style={{ paddingTop: "4px" }}>
        {feeValue
          ? <span style={{ fontSize: "14px", fontWeight: 700, color: "#16a34a" }}>
              ${feeValue.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          : <span style={{ fontSize: "13px", color: "#cbd5e1" }}>—</span>}
      </div>

      {/* Status */}
      <div style={{ paddingTop: "2px" }}>
        <StatusPill status={currentStatus} onClick={handleToggleStatus} loading={saving} />
      </div>

      {/* Schedule rule */}
      <div>
        {editRule ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", gap: "4px" }}>
              <select value={ruleOcc} onChange={e => setRuleOcc(e.target.value)}
                style={{ fontSize: "11px", padding: "3px 4px", border: `1px solid ${colors.border}`,
                  borderRadius: "4px", flex: 1, backgroundColor: colors.white }}>
                <option value="">Occur.</option>
                <option value="first">First</option>
                <option value="second">Second</option>
                <option value="third">Third</option>
                <option value="fourth">Fourth</option>
                <option value="last">Last</option>
              </select>
              <select value={ruleWd} onChange={e => setRuleWd(e.target.value)}
                style={{ fontSize: "11px", padding: "3px 4px", border: `1px solid ${colors.border}`,
                  borderRadius: "4px", flex: 1, backgroundColor: colors.white }}>
                <option value="">Day</option>
                <option value="monday">Mon</option>
                <option value="tuesday">Tue</option>
                <option value="wednesday">Wed</option>
                <option value="thursday">Thu</option>
                <option value="friday">Fri</option>
                <option value="saturday">Sat</option>
                <option value="sunday">Sun</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <button onClick={handleSaveRule} disabled={savingRule || !ruleOcc || !ruleWd}
                style={{ fontSize: "10px", padding: "2px 8px", border: "none", borderRadius: "4px",
                  backgroundColor: colors.blue, color: colors.white,
                  cursor: (!ruleOcc || !ruleWd) ? "default" : "pointer",
                  opacity: (!ruleOcc || !ruleWd) ? 0.5 : 1 }}>
                {savingRule ? "…" : "Save"}
              </button>
              <button onClick={handleCancelRule}
                style={{ fontSize: "10px", padding: "2px 6px", border: `1px solid ${colors.border}`,
                  borderRadius: "4px", background: "none", cursor: "pointer" }}>✕</button>
            </div>
          </div>
        ) : hasRule ? (
          <span onClick={() => setEditRule(true)} title="Click to edit"
            style={{ fontSize: "11px", fontWeight: 700, padding: "3px 9px", borderRadius: "20px",
              backgroundColor: "#ede9fe", color: "#6d28d9",
              cursor: "pointer", userSelect: "none", display: "inline-block",
              border: "1px solid #c4b5fd" }}>
            {OCCURRENCE_LABELS[site.monthly_schedule_occurrence]} {WEEKDAY_LABELS[site.monthly_schedule_weekday]}
          </span>
        ) : (
          <span onClick={() => setEditRule(true)}
            style={{ fontSize: "12px", color: "#94a3b8", cursor: "pointer",
              userSelect: "none", fontStyle: "italic" }}>
            + Set rule
          </span>
        )}
      </div>

      {/* Scheduled date */}
      <div>
        <input
          type="datetime-local"
          value={dateVal}
          onChange={handleDateChange}
          disabled={saving}
          style={{
            fontSize: "12px", padding: "5px 8px",
            border: `1px solid ${colors.border}`, borderRadius: "6px",
            color: colors.text, backgroundColor: colors.white,
            width: "100%", outline: "none",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
          }}
        />
        {suggestedStr && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "5px" }}>
            <span style={{ fontSize: "11px", color: colors.slate }}>
              {OCCURRENCE_LABELS[site.monthly_schedule_occurrence]}{" "}
              {WEEKDAY_LABELS[site.monthly_schedule_weekday]}{" · "}
              {suggestedDate.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
            </span>
            <button
              onClick={handleUseSchedule}
              disabled={saving || dateVal === suggestedStr}
              style={{
                fontSize: "10px", padding: "2px 8px",
                border: `1px solid ${dateVal === suggestedStr ? "#86efac" : colors.border}`,
                borderRadius: "4px",
                cursor: dateVal === suggestedStr ? "default" : "pointer",
                background: dateVal === suggestedStr ? "#dcfce7" : colors.white,
                color: dateVal === suggestedStr ? "#16a34a" : colors.text,
                fontWeight: dateVal === suggestedStr ? 600 : 400,
              }}
            >{dateVal === suggestedStr ? "✓ Set" : "Use"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function MonthlyServicePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthTab, setMonthTab] = useState(now.getMonth());
  const [sites, setSites] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sitesRes, recsRes] = await Promise.all([
        axios.get(`${API}/sites/`),
        axios.get(`${API}/monthly-services/?year=${year}&month=${monthTab + 1}`),
      ]);
      setSites((sitesRes.data || []).filter(s => s.has_monthly_service || s.has_monthly_efsm));
      setRecords(recsRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [year, monthTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const recordBySiteId = Object.fromEntries(records.map(r => [r.site, r]));

  const handleUpdated = (updated) => {
    setRecords(prev => {
      const idx = prev.findIndex(r => r.id === updated.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = updated; return next; }
      return [...prev, updated];
    });
  };

  const handleSiteUpdated = (updated) => {
    setSites(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const counts = {
    done:      records.filter(r => r.status === "done").length,
    scheduled: records.filter(r => r.status === "scheduled").length,
    overdue:   records.filter(r => r.status === "overdue").length,
    due: sites.filter(s => { const r = recordBySiteId[s.id]; return !r || r.status === "due"; }).length,
  };
  const monthlyTotal = sites.reduce((sum, s) => sum + parseFloat(s.monthly_fee || 0), 0);
  const fmtMoney = n => `$${Math.round(n).toLocaleString("en-AU")}`;

  return (
    <div style={{ padding: "28px 28px 40px", maxWidth: "1300px", margin: "0 auto" }}>

      {/* Page title + year nav */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: colors.text, letterSpacing: "-0.3px" }}>
          Monthly Services
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginLeft: "4px" }}>
          <button onClick={() => setYear(y => y - 1)}
            style={{ ...iconBtnStyle, width: "28px", height: "28px", fontSize: "16px" }}>‹</button>
          <span style={{ fontSize: "15px", fontWeight: 700, color: colors.text, minWidth: "44px", textAlign: "center" }}>
            {year}
          </span>
          <button onClick={() => setYear(y => y + 1)}
            style={{ ...iconBtnStyle, width: "28px", height: "28px", fontSize: "16px" }}>›</button>
        </div>
      </div>

      {/* Month tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "20px", flexWrap: "wrap" }}>
        {MONTH_SHORT.map((m, i) => (
          <button key={i} onClick={() => setMonthTab(i)} style={{
            padding: "6px 12px", fontSize: "13px",
            fontWeight: monthTab === i ? 700 : 400,
            borderRadius: "20px",
            border: monthTab === i ? "none" : `1px solid ${colors.border}`,
            cursor: "pointer",
            backgroundColor: monthTab === i ? colors.red : colors.white,
            color: monthTab === i ? colors.white : colors.text,
            boxShadow: monthTab === i ? "0 2px 8px rgba(230,57,70,0.3)" : "none",
            transition: "all 0.15s",
          }}>{m}</button>
        ))}
      </div>

      {/* KPI strip */}
      {!loading && (
        <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
          {/* Monthly value — hero card */}
          <div style={{
            background: "#1e293b", borderRadius: "10px", padding: "14px 20px",
            flex: "1.4", minWidth: "140px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#4ade80", lineHeight: 1 }}>
              {fmtMoney(monthlyTotal)}
            </div>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.55)", marginTop: "5px",
              fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Monthly Value · {sites.length} site{sites.length !== 1 ? "s" : ""}
            </div>
          </div>
          <KpiChip label="Done" value={counts.done} color="#16a34a" />
          <KpiChip label="Scheduled" value={counts.scheduled} color="#d97706" />
          <KpiChip label="Due" value={counts.due} color="#1e40af" />
          <KpiChip label="Overdue" value={counts.overdue} color="#dc2626" />
        </div>
      )}

      {/* Table card */}
      <div style={{
        borderRadius: "12px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
      }}>
        {/* Table header */}
        {!loading && sites.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: ROW_GRID, gap: "12px",
            padding: "10px 20px",
            backgroundColor: "#f8fafc",
            borderBottom: `2px solid ${colors.border}`,
            borderLeft: "3px solid transparent",
          }}>
            {["Site", "Account", "Value", "Status", "Schedule", "Scheduled Date & Time"].map(h => (
              <div key={h} style={{ fontSize: "10px", fontWeight: 800, color: "#94a3b8",
                textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {h}
              </div>
            ))}
          </div>
        )}

        {/* Rows */}
        {loading ? (
          <div style={{ padding: "48px", textAlign: "center", color: colors.slate }}>
            <div style={{ fontSize: "14px" }}>Loading…</div>
          </div>
        ) : sites.length === 0 ? (
          <div style={{ padding: "56px 40px", textAlign: "center", color: colors.slate }}>
            <div style={{ fontSize: "15px", fontWeight: 600, marginBottom: "8px", color: colors.text }}>
              No sites with monthly service
            </div>
            <div style={{ fontSize: "13px" }}>
              Enable "Monthly Service" on a site's baseline settings to add it here.
            </div>
          </div>
        ) : (
          <>
            {sites.map(site => (
              <SiteRow
                key={site.id}
                site={site}
                record={recordBySiteId[site.id] || null}
                monthNum={monthTab + 1}
                year={year}
                onUpdated={handleUpdated}
                onSiteUpdated={handleSiteUpdated}
              />
            ))}

            {/* Footer total */}
            <div style={{
              display: "grid", gridTemplateColumns: ROW_GRID, gap: "12px",
              padding: "14px 20px",
              backgroundColor: "#1e293b",
              borderLeft: "3px solid #4ade80",
            }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "rgba(255,255,255,0.7)",
                gridColumn: "1 / 3", paddingTop: "2px" }}>
                Total — {sites.length} site{sites.length !== 1 ? "s" : ""}
              </div>
              <div style={{ fontSize: "18px", fontWeight: 800, color: "#4ade80" }}>
                {fmtMoney(monthlyTotal)}
              </div>
              <div /><div /><div />
            </div>
          </>
        )}
      </div>

    </div>
  );
}
